import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { nanoid } from "nanoid";
import { useTranslation } from "react-i18next";

// Forms
import { selfEfficacy, dementiaLiteracy, attitudesTowardDementia } from "../attitudeAwareness/attitudeAwarenessForms";
import { cognitiveEngagement, yourNutrition, visionHearing } from "../lifestyle/lifestyleForms";

// Custom components
import Title from "../../../../general/Title";
import TopBarForm from "../TopBarForm";
import InstructionDialog from "../InstructionDialog";

// Mui core
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import IconButton from "@mui/material/IconButton";

// Icons
import { GrCircleInformation } from "react-icons/gr";
import { MdKeyboardArrowRight } from "react-icons/md";

// Hooks
import RadioGroup from "@mui/material/RadioGroup";

// Validation
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// Utils
import { dirtyValues, removeUndefinedInObject } from "../../../../../utils/general";
import { getScoreByQuestion } from "../../../../../utils/score";
import { Participant } from "../../../../../interfaces/Participant";

const requiredMessage = "Required field";

interface Question {
    questionFR: string;
    questionEN: string;
    correctAnswer: string;
    answers: Answer[];
    commentFR?: string;
    commentEN?: string;
}

interface Answer {
    labelFR: string;
    labelEN: string;
    pointValue?: number;
}

interface Form {
    titleFR: string;
    titleEN: string;
    aboutFR: string;
    aboutEN: string;
    instructionsFR: string;
    instructionsEN: string;
    questions: Question[];
    answers: Answer[];
}

const generateSchema = (form: Form) => {
    const conditions: any = {};
    form.questions.forEach((q, i) => {
        if (!q.commentEN) {
            conditions[i.toString()] = yup.string().required(requiredMessage);
        }
    });
    return yup.object(conditions);
};

const generateDefaultValues = (form: Form) => {
    const output: any = {};
    form.questions.forEach((q, i) => {
        if (!q.commentEN) {
            output[i] = "";
        }
    });
    return output;
};

interface Props {
    form: Form;
    // eslint-disable-next-line
    onComplete: (title: string, data: any, points: number, topic: string, participant?: Participant | undefined) => void;
}

const AttitudeAwarenessFormGenerator: React.FC<Props> = ({ form, onComplete }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    // States
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [isSafeToReset, setIsSafeToReset] = useState(false);

    // Forms
    const {
        reset,
        handleSubmit,
        formState: { errors, dirtyFields },
        control,
    } = useForm({ resolver: yupResolver(generateSchema(form)), defaultValues: generateDefaultValues(form) });

    const onSubmit = async (data: any) => {
        try {
            let points = 0;
            let topic = "";
            data = removeUndefinedInObject(data);

            // Get topic from form
            if (form === selfEfficacy) topic = "Self Efficacy";
            if (form === dementiaLiteracy) topic = "Dementia Literacy";
            if (form === attitudesTowardDementia) topic = "Attitudes towards dementia";
            if (form === cognitiveEngagement) topic = "Cognitive";
            if (form === yourNutrition) topic = "Nutrition";
            if (form === visionHearing) topic = "Vision Hearing";

            Object.entries(dirtyValues(dirtyFields, data)).forEach(e => {
                const localPoints = getScoreByQuestion(topic, parseInt((e[0] as string).replace("question", "")), dirtyValues(dirtyFields, data));
                if (typeof localPoints !== "undefined" && localPoints !== "n/a") points += localPoints as number;
            });

            enqueueSnackbar(t("questionnaireCompleted", { title: localStorage.getItem("language") === "en" ? form.titleEN : form.titleFR }), {
                variant: "success",
            });
            onComplete(form.titleEN, dirtyValues(dirtyFields, data), points, topic);
            window.scrollTo(0, 0);
            setIsSafeToReset(true);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (Object.entries(errors).length > 0) {
            const id = Object.entries(errors)[0][0];
            const element = document.getElementById(id);

            if (element) element.scrollIntoView({ block: "center" });
        }
    }, [errors]);

    useEffect(() => {
        if (isSafeToReset) reset();
    }, [isSafeToReset]);

    return (
        <>
            <TopBarForm />
            <div className="forms__container">
                <Title>{localStorage.getItem("language") === "en" ? form.titleEN : form.titleFR}</Title>

                {form.aboutEN && (
                    <div className="forms__section">
                        <h6 className="forms__subtitle">{localStorage.getItem("language") === "en" ? "About" : "À propos"}</h6>
                        <p>{localStorage.getItem("language") === "en" ? form.aboutEN : form.aboutFR}</p>
                    </div>
                )}

                {form.instructionsEN && (
                    <div className="forms__section">
                        <h6 className="forms__subtitle">Instructions</h6>
                        <p>{localStorage.getItem("language") === "en" ? form.instructionsEN : form.instructionsFR}</p>
                    </div>
                )}

                <form
                    noValidate
                    autoComplete="off"
                    onSubmit={handleSubmit(onSubmit)}
                    onKeyDown={(e: any) => {
                        // Disable submit on "enter" key press
                        e.key === "Enter" && e.preventDefault();
                    }}
                    className="forms__content"
                >
                    <Grid item>
                        {form.questions.map((q, i) => {
                            if (q.commentEN) {
                                return (
                                    <div key={i + nanoid()} className="forms__important__container">
                                        <h6 className="forms__important">
                                            &emsp;{localStorage.getItem("language") === "en" ? q.commentEN : q.commentFR}
                                        </h6>
                                    </div>
                                );
                            } else {
                                return (
                                    <div key={q.questionEN + i + nanoid()}>
                                        <Controller
                                            control={control}
                                            name={i.toString().trim()}
                                            render={({ field }) => (
                                                <FormControl
                                                    id={i.toString()}
                                                    component="fieldset"
                                                    error={!!errors[i]?.message}
                                                    className="forms__fullWidth"
                                                >
                                                    <FormLabel component="legend">
                                                        <>
                                                            {localStorage.getItem("language") === "en" ? q.questionEN.trim() : q.questionFR.trim()}
                                                            <IconButton
                                                                color="primary"
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => {
                                                                    setSelectedTypes(["radio"]);
                                                                    setDialogOpen(true);
                                                                }}
                                                            >
                                                                <GrCircleInformation />
                                                            </IconButton>
                                                        </>
                                                    </FormLabel>
                                                    <RadioGroup aria-label="gender" row {...field} className="forms__radioGroupContainer">
                                                        {!q.answers.length
                                                            ? form.answers.map(a => (
                                                                  <FormControlLabel
                                                                      key={a.labelEN + i + nanoid()}
                                                                      value={a.labelEN}
                                                                      control={<Radio />}
                                                                      label={localStorage.getItem("language") === "en" ? a.labelEN : a.labelFR}
                                                                  />
                                                              ))
                                                            : q.answers.map(a => (
                                                                  <FormControlLabel
                                                                      key={a.labelEN + i + nanoid()}
                                                                      value={a.labelEN}
                                                                      control={<Radio />}
                                                                      label={localStorage.getItem("language") === "en" ? a.labelEN : a.labelFR}
                                                                  />
                                                              ))}
                                                    </RadioGroup>
                                                    <FormHelperText style={{ fontSize: 20 }}>{errors[i]?.message}</FormHelperText>
                                                </FormControl>
                                            )}
                                        />
                                    </div>
                                );
                            }
                        })}
                    </Grid>
                    <Grid item>
                        <Button variant="contained" type="submit" className="questionnaires__cta" fullWidth>
                            {localStorage.getItem("language") === "en" ? "Submit" : "Soumettre"} <MdKeyboardArrowRight />
                        </Button>
                    </Grid>
                    <div id="spacer_for_button" style={{ minHeight: 30 }} />
                </form>
            </div>
            <InstructionDialog open={dialogOpen} setOpen={setDialogOpen} types={selectedTypes} />
        </>
    );
};

export default AttitudeAwarenessFormGenerator;
